// import { useClient, useQuery } from '@based/sdk/react'
// import { useCallback, useEffect, useMemo, useState } from 'react'
// import { FinderView } from '../components/BasedFinder/index.js'
// // import { FilterOl as Filter, Sortasdsa as Sort } from '../utils/common.js'
// import { getCalendarVisiblePeriod } from '../components/Calendar/index.js'
// import { useBasedEndpoints } from '../providers/Endpoints/index.js'

import { useClient } from '@based/sdk/react'
import { useState, useMemo, useCallback } from 'react'
import { useBasedEndpoints } from '../providers/Endpoints/index.js'
import { Filter } from '../components/FilterInput/index.js'
import { Sort } from '../components/SortInput/index.js'

type QueryOptions = {
  // view: FinderView
  // for table and grid
  offset?: number
  limit?: number
  sort?: Sort
  filter?: Filter
  // for calendar
  start?: number
  end?: number
  lang?: string
  search?: string
}

export type UseInfiniteQueryOptions = {
  sort?: Sort
  filter?: Filter
  query: (options: QueryOptions) => any
  transformQueryResult?: (queryResult: any) => any
}

// type Chunk = {
//   offset: number
//   limit: number
//   data?: any[]
//   unsubscribe?: () => void
// }

// type CalendarChunk = {
//   data?: any[]
//   unsubscribe?: () => void
// }

let firstRun = true

function useInfiniteQuery({
  sort,
  filter,
  query,
  transformQueryResult,
}: UseInfiniteQueryOptions) {
  const endpoints = useBasedEndpoints()
  const client = useClient()
  const [chunks, setChunks] = useState<any[]>([])
  const [checksum, setChecksum] = useState<number>()
  const [scroll, setScroll] = useState({
    first: 0,
    last: 0,
    actualFirst: 0,
    actualLast: 0,
  })
  const chunkSize = 128

  const data = useMemo(() => {
    const { first, last } = scroll
    const res: any[] = []

    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i]
      if (!chunk?.data) continue
      const unwrappedData = transformQueryResult?.(chunk.data) || chunk.data

      for (let j = 0; j < unwrappedData.length; j++) {
        if (chunk.offset + j >= first && chunk.offset + j <= last) {
          res.push({ ...unwrappedData[j] })
        }
      }
    }

    return res
  }, [scroll, chunks])

  const getChunk = useCallback(
    async ({ start, pageSize }: Record<string, number>) => {
      if (!pageSize) return

      pageSize = pageSize * 4

      const data = await client
        .query(
          endpoints.query,
          query({
            start,
            limit: pageSize,
            offset: 0,
          }),
        )
        .get()

      setScroll({
        first: start,
        last: pageSize,
        actualFirst: start,
        actualLast: pageSize,
      })

      setChunks((p) => {
        const c = [...p]
        c[0] = { offset: 0, limit: pageSize, data }
        return c
      })

      if (data.length) {
        firstRun = false
      }
    },
    [query, client],
  )

  const getChunksByScroll = useCallback(
    ({ first, last, actualFirst, actualLast }: Record<string, number>) => {
      setScroll({
        first,
        last,
        actualFirst,
        actualLast,
      })

      const firstChunk = Math.floor(first / chunkSize)
      const lastChunk = Math.ceil(last / chunkSize)

      for (let i = firstChunk; i < lastChunk; i++) {
        if (!chunks[i]) {
          setChunks((p) => {
            const c = [...p]
            c[i] = { offset: i * chunkSize, limit: chunkSize }
            return c
          })
        }

        const offset = i * chunkSize
        const limit = chunkSize
        const unsubscribe = client
          .query(
            endpoints.query,
            query({
              offset,
              limit,
              sort,
              filter,
            }),
          )
          .subscribe((data, checksum) => {
            setChecksum(checksum)

            setChunks((p) => {
              const c = [...p]
              c[i] = { offset, limit, unsubscribe, data }
              return c
            })
          })
      }

      for (let j = 0; j < chunks.length; j++) {
        if (firstChunk > j || lastChunk < j) {
          const chunk = chunks[j]

          if (chunk) {
            chunk.unsubscribe?.()
          }
        }
      }
    },
    [query, client, sort, filter],
  )

  const reset = useCallback(() => {
    setScroll({
      first: 0,
      last: 0,
      actualFirst: 0,
      actualLast: 0,
    })
    setChunks([])
  }, [])

  return {
    data,
    checksum,
    getChunksByScroll,
    getChunk,
    scroll,
    reset,
  }
}

export { useInfiniteQuery }
// export type { UseInfiniteQueryOptions }
